import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  AlertTriangle, Building2, Download, FileSignature, FileText, Lock, Receipt, Search,
} from 'lucide-react'

const API_BASE = import.meta.env.VITE_PROJECTS_API_URL || '/api/projects'

/**
 * A client's own library of everything one tenant holds for them.
 *
 * PUBLIC AND READ-ONLY. The token in the url is the credential, and it lives on the `crm_clients`
 * row — already (tenant, client) — so one tenant's link can never reach another tenant's documents
 * for the same company. What appears is decided server-side; this page never filters by name or
 * type, because a rule applied in the viewer stops applying the moment somebody fetches the data
 * another way.
 *
 * GROUPED BY TYPE, NOT BY JOB, and not flat either. Both of those were tried and both were wrong:
 * grouping by project made a client library read as a project folder (*"it looks like it is
 * documents per project not documents per client"*), and flattening it gave too little to go on
 * (*"I do not want a flat list... more information needs to be provided"*). Type is how a person
 * actually looks for a document — "the invoice", "the ticket I signed" — and the job becomes a
 * filter, which is what helps a client with forty of them.
 *
 * IT MUST SAY WHOSE DOCUMENTS THESE ARE without being read closely. A client arrives from an email,
 * often on a phone, and the first question is "is this mine, and who sent it".
 */
export default function ClientDocumentFolder() {
  const { slug, code } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState('')
  const [job, setJob] = useState(ALL_JOBS)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/client/folder/${encodeURIComponent(slug)}/${encodeURIComponent(code)}`)
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.detail || 'This link is no longer valid.')
        }
        const body = await res.json()
        if (!cancelled) setData(body)
      } catch (e) {
        if (!cancelled) setError(e.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [slug, code])

  const sections = data?.sections || []
  const all = useMemo(() => sections.flatMap(s => s.items || []), [sections])
  const brand = data?.brand || {}
  const accent = brand.accent_color || '#1d4ed8'
  const headerBg = brand.header_bg || '#0f172a'

  // A filter, not a heading. Shown only when there is more than one job to choose between.
  const jobs = useMemo(
    () => [ALL_JOBS, ...[...new Set(all.map(jobLabel).filter(Boolean))]], [all])

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const keep = (d) => {
      if (job !== ALL_JOBS && jobLabel(d) !== job) return false
      if (!needle) return true
      return [d.title, d.kind, d.project_number, d.project_name, d.signed_by]
        .filter(Boolean).some(v => String(v).toLowerCase().includes(needle))
    }
    return sections
      .map(s => ({ ...s, items: (s.items || []).filter(keep) }))
      .filter(s => s.items.length > 0)
  }, [sections, query, job])

  const span = useMemo(() => {
    const dates = all.map(d => d.work_date || d.created_at).filter(Boolean).sort()
    if (!dates.length) return null
    const a = fmtDate(dates[0]); const b = fmtDate(dates[dates.length - 1])
    return a === b ? a : `${a} – ${b}`
  }, [all])

  const awaiting = sections.find(s => s.key === 'awaiting')?.items?.length || 0

  if (loading) {
    return <Frame brand={{}}><p className="py-16 text-sm text-slate-500">Loading your documents…</p></Frame>
  }

  if (error) {
    return (
      <Frame brand={{}}>
        <div className="mt-10 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-medium">{error}</p>
            <p className="mt-1">Contact the company that sent this and they can issue a new link.</p>
          </div>
        </div>
      </Frame>
    )
  }

  return (
    <Frame brand={brand} headerBg={headerBg}>
      <section className="border-b border-slate-200 py-8">
        <p className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: accent }}>
          Document library
        </p>
        <h1 className="mt-1.5 text-[26px] font-semibold leading-tight tracking-tight text-slate-900">
          Documents for {data.company_name || 'your company'}
        </h1>
        <p className="mt-2 flex items-center gap-2 text-sm text-slate-600">
          <Building2 className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
          Provided by <span className="font-medium text-slate-900">{brand.company_name || 'us'}</span>
        </p>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">
          Everything we hold for you, across all of your jobs with us — not one project. This is your
          copy to read, download and keep; nothing here can be changed.
        </p>

        {all.length > 0 && (
          <dl className="mt-5 flex flex-wrap gap-x-10 gap-y-3">
            <Stat label="Items" value={all.length} />
            {span && <Stat label="Covering" value={span} />}
            {jobs.length > 2 && <Stat label="Jobs" value={jobs.length - 1} />}
          </dl>
        )}

        {/* The only thing on this page that asks anything of the reader, so it is said once, up
            front, rather than left to be discovered halfway down. */}
        {awaiting > 0 && (
          <div className="mt-5 flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-3 text-sm text-amber-900">
            <FileSignature className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <p>
              <span className="font-semibold">
                {awaiting} {awaiting === 1 ? 'document is' : 'documents are'} waiting for your signature.
              </span>{' '}
              Use “Email me the signing link” below and we’ll send it to the address we have on
              file for you.
            </p>
          </div>
        )}
      </section>

      {all.length === 0 ? (
        <Empty>Nothing here yet. Signed documents appear automatically as work is completed and approved.</Empty>
      ) : (
        <>
          {(all.length > 6 || jobs.length > 2) && (
            <div className="flex flex-wrap items-center justify-between gap-3 py-4">
              <div className="flex flex-wrap gap-1.5">
                {jobs.length > 2 && jobs.map(j => (
                  <button
                    key={j}
                    onClick={() => setJob(j)}
                    className={`max-w-[16rem] truncate rounded-full border px-3 py-1 text-xs font-medium transition ${
                      job === j ? 'border-transparent text-white'
                                : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'}`}
                    style={job === j ? { backgroundColor: accent } : undefined}
                  >
                    {j}
                  </button>
                ))}
              </div>
              {all.length > 6 && (
                <label className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                  <input
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Search"
                    className="w-52 rounded-md border border-slate-300 py-1.5 pl-8 pr-3 text-sm placeholder:text-slate-400 focus:border-slate-400 focus:outline-none"
                  />
                </label>
              )}
            </div>
          )}

          {visible.length === 0 ? (
            <Empty>Nothing matches that.</Empty>
          ) : (
            <div className="space-y-7 pt-1">
              {visible.map(sec => (
                <section key={sec.key}>
                  <div className="flex items-baseline gap-2">
                    <h2 className="text-sm font-semibold text-slate-900">{sec.title}</h2>
                    <span className="text-xs text-slate-500">{sec.items.length}</span>
                  </div>
                  {sec.note && <p className="mt-0.5 text-xs text-slate-500">{sec.note}</p>}
                  <ul className="mt-2 divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    {sec.items.map(d => (
                      <Row key={`${sec.key}-${d.id}`} d={d} sec={sec} accent={accent}
                           slug={slug} code={code} />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </>
      )}

      <footer className="mt-10 flex items-start gap-2.5 border-t border-slate-200 pt-5 text-xs leading-relaxed text-slate-500">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
        <p>
          This is a private link to {data.company_name ? `${data.company_name}'s` : 'your'} documents
          alone. Keep it — it stays valid, and new documents appear here automatically.
          {brand.company_name && <> If you were not expecting it, contact {brand.company_name}.</>}
        </p>
      </footer>
    </Frame>
  )
}

function Row({ d, sec, accent, slug, code }) {
  const pending = sec.key === 'awaiting'
  const [sending, setSending] = useState(null)   // null | 'sending' | {sent_to} | {error}

  // "Email me the link" rather than a link here. The library is standing access that gets
  // forwarded; a signing credential must not be. It goes to the address on the client record and
  // nowhere else, so only control of that mailbox can approve work — and the client can still act,
  // which a read-only "awaiting signature" label alone did not let them do.
  //
  // NO ADDRESS BOX. There was one, prefilled and editable. It meant anyone holding this (standing,
  // forwardable) folder link could have a signing credential delivered to a mailbox they chose.
  // The server now ignores any address sent here; the input is gone so the page does not imply
  // otherwise. Staff can still send to an alternate address from inside the app, behind a login.
  const requestSigningLink = async () => {
    setSending('sending')
    try {
      const res = await fetch(
        `${API_BASE}/client/folder/${encodeURIComponent(slug)}/${encodeURIComponent(code)}/request-signing-link`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ticket_number: d.id }) })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.detail || 'We could not send that just now.')
      setSending({ sent_to: body.sent_to })
    } catch (e) {
      setSending({ error: e.message })
    }
  }
  const Icon = sec.key === 'invoices' ? Receipt : pending ? FileSignature : FileText
  const href = sec.key === 'invoices'
    ? `${API_BASE}/client/folder/${encodeURIComponent(slug)}/${encodeURIComponent(code)}/invoice/${encodeURIComponent(d.id)}`
    : `${API_BASE}/client/folder/${encodeURIComponent(slug)}/${encodeURIComponent(code)}/document/${encodeURIComponent(d.id)}`

  const detail = [
    d.work_date && `Work performed ${fmtDate(d.work_date)}`,
    d.signed_by && `Signed by ${d.signed_by}`,
    d.signed_at && fmtDate(d.signed_at),
    d.amount != null && fmtMoney(d.amount),
    d.period?.[0] && `${fmtDate(d.period[0])} – ${fmtDate(d.period[1])}`,
    d.ready_since && `Ready since ${fmtDate(d.ready_since)}`,
    fmtSize(d.size_bytes),
  ].filter(Boolean).join(' · ')

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 transition hover:bg-slate-50/60">
      <div className="flex min-w-0 items-start gap-3.5">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: `${accent}14`, color: accent }}>
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 text-[15px] font-semibold text-slate-900">
            <span className="truncate">{d.title}</span>
            {/* MARKED, NOT HIDDEN. A client holding a superseded copy needs to know it is not the
                current one — taking it away would leave them with the old figures and no reason. */}
            {d.superseded && (
              <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-900">
                Replaced — ask us for the current version
              </span>
            )}
          </p>
          {jobLabel(d) && <p className="mt-0.5 truncate text-xs text-slate-500">{jobLabel(d)}</p>}
          {detail && <p className="mt-1 truncate text-xs text-slate-500">{detail}</p>}
        </div>
      </div>
      {pending ? (
        <div className="shrink-0 text-right">
          {sending && sending.sent_to ? (
            <p className="text-xs text-slate-600">
              Link sent to <span className="font-medium">{sending.sent_to}</span>
            </p>
          ) : (
            <button
              onClick={requestSigningLink}
              disabled={sending === 'sending'}
              className="rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              {sending === 'sending' ? 'Sending…' : 'Email me the signing link'}
            </button>
          )}
          {!sending && (
            // Say WHERE it goes before they press it, so "email me the link" is not a mystery.
            <p className="mt-1 text-xs text-slate-500">
              We’ll send it to the address we have on file for you.
            </p>
          )}
          {sending && sending.error && (
            <p className="mt-1 text-xs text-red-700">{sending.error}</p>
          )}
        </div>
      ) : (
        <a href={href} target="_blank" rel="noreferrer"
           className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold text-white transition hover:opacity-90"
           style={{ backgroundColor: accent }}>
          <Download className="h-4 w-4" aria-hidden="true" />
          Open
        </a>
      )}
    </li>
  )
}

/** The tenant's identity at the top, in their own colour, so a client knows at a glance who sent it. */
function Frame({ brand, headerBg = '#0f172a', children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="px-4 py-4" style={{ backgroundColor: headerBg }}>
        <div className="mx-auto flex w-full max-w-3xl items-center">
          {brand?.logo_url ? (
            <img src={brand.logo_url} alt={brand.company_name || ''} className="h-8 max-w-[220px] object-contain" />
          ) : (
            <span className="text-[17px] font-semibold tracking-tight text-white">
              {brand?.company_name || ' '}
            </span>
          )}
        </div>
      </header>
      <main className="mx-auto w-full max-w-3xl px-4 pb-20">{children}</main>
    </div>
  )
}

function Stat({ label, value }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm font-semibold text-slate-900">{value}</dd>
    </div>
  )
}

function Empty({ children }) {
  return (
    <div className="mt-8 rounded-xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
      <FileText className="mx-auto h-6 w-6 text-slate-300" aria-hidden="true" />
      <p className="mx-auto mt-3 max-w-sm text-sm text-slate-600">{children}</p>
    </div>
  )
}

const ALL_JOBS = 'All jobs'
const jobLabel = (d) => [d.project_number, d.project_name].filter(Boolean).join(' — ') || null

const fmtDate = (v) => {
  if (!v) return ''
  // A date-only value renders at local midnight so it cannot slip a day across time zones — the
  // work date on a ticket is a calendar date, not an instant.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(`${v}T00:00:00`) : new Date(v)
  return isNaN(d) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const fmtMoney = (n) =>
  typeof n === 'number'
    ? n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })
    : ''

const fmtSize = (bytes) => {
  if (!bytes || bytes < 1024) return ''
  const kb = bytes / 1024
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`
}
